import { useState, useEffect, useCallback } from 'react'
import { STORAGE_KEY, STORAGE_KEY_CORRUPTED, parseStoredValue } from './selectedPlayerStorage'

/**
 * localStorage から選択中選手IDを読み出す。
 * 読み込みに失敗した場合（localStorage 自体が使えない）や、値が壊れている場合でも
 * 例外を投げず、値を消さずに退避したうえで未選択（null）として扱う（SPEC §2.2）。
 */
function readStoredPlayerId(): string | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    // localStorage 自体にアクセスできない（プライベートモード等）。未選択として扱う
    return null
  }

  const parsed = parseStoredValue(raw)
  if (parsed !== null || raw === null) return parsed

  // 破損データ: 削除せず退避キーへ移し、未選択状態として描画する（SPEC §2.2）
  try {
    localStorage.setItem(STORAGE_KEY_CORRUPTED, raw)
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // 退避にも失敗した場合でも画面は壊さない
  }
  return null
}

function writeStoredPlayerId(id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(STORAGE_KEY, id)
    }
  } catch {
    // localStorage 使用不可（プライベートモード等）は無視。画面は壊さない
  }
}

interface UseFavoritePlayerReturn {
  favoritePlayerId: string | null
  setFavoritePlayer: (id: string) => void
  clearFavoritePlayer: () => void
}

/**
 * お気に入り選手IDを localStorage に永続化するフック（SPEC §2.2）。
 * - キーはバージョン付き（`oshi-viewer:v1:selectedPlayer`）
 * - 破損データは消さず退避する
 * - 複数タブで一方が変更したら `storage` イベントで他タブにも反映する
 *   （「最後に書いた方が勝つ」調停）
 */
export function useFavoritePlayer(): UseFavoritePlayerReturn {
  const [favoritePlayerId, setFavoritePlayerIdState] = useState<string | null>(
    () => readStoredPlayerId(),
  )

  useEffect(() => {
    function handleStorage(event: StorageEvent): void {
      // 他タブでの変更のみ反映する（同一キー、または全消去を示す key=null）
      if (event.key !== null && event.key !== STORAGE_KEY) return
      setFavoritePlayerIdState(readStoredPlayerId())
    }

    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const setFavoritePlayer = useCallback((id: string) => {
    writeStoredPlayerId(id)
    setFavoritePlayerIdState(id)
  }, [])

  const clearFavoritePlayer = useCallback(() => {
    writeStoredPlayerId(null)
    setFavoritePlayerIdState(null)
  }, [])

  return { favoritePlayerId, setFavoritePlayer, clearFavoritePlayer }
}
